import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, TextField, MenuItem, Typography, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Avatar,
  Tabs, Tab, Divider, Stack, Card, CardContent, IconButton, Tooltip,
  LinearProgress, Stepper, Step, StepLabel, StepConnector,
  stepConnectorClasses, Alert, AlertTitle, InputAdornment, CircularProgress,
} from "@mui/material";
import {
  Search, Refresh, Visibility, CheckCircle, Cancel, LocalShipping,
  Inventory, AttachMoney, HourglassEmpty, CloudUpload, DeleteOutline,
  CalendarToday, Phone, Email, LocationOn, Receipt, FilterList,
  PlayArrow,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { toast } from "react-toastify";
import apiClient from "../utils/apiClient";
import { getImageFullUrl, pageRoute } from "../utils/common";
import { useNavigate, useSearchParams } from "react-router-dom";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_FLOW = ["pending", "confirmed", "processing", "shipped", "delivered", "cancelled", 'returned'];

const STATUS_CONFIG = {
  pending: { color: "warning", label: "Pending", icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  confirmed: { color: "info", label: "Confirmed", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  processing: { color: "secondary", label: "Processing", icon: <Inventory sx={{ fontSize: 14 }} /> },
  shipped: { color: "primary", label: "Shipped", icon: <LocalShipping sx={{ fontSize: 14 }} /> },
  delivered: { color: "success", label: "Delivered", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  cancelled: { color: "error", label: "Cancelled", icon: <Cancel sx={{ fontSize: 14 }} /> },
  returned: { color: "error", label: "Returned", icon: <Cancel sx={{ fontSize: 14 }} /> },
};

const PAYMENT_CONFIG = {
  paid: { color: "success", label: "Paid" },
  pending: { color: "warning", label: "Pending" },
  failed: { color: "error", label: "Failed" },
  refunded: { color: "info", label: "Refunded" },
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const HoverRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  transition: "background .15s",
  "&:hover": { background: alpha(theme.palette.primary.main, 0.04) },
  "&:last-child td": { border: 0 },
}));
// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    })
    : "—";

const parseJSON = (str, fallback = []) => {
  try { return typeof str === "string" ? JSON.parse(str) : str || fallback; }
  catch { return fallback; }
};

const initials = (name = "") =>
  (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

// ─── Section Title ────────────────────────────────────────────────────────────

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      fontWeight={700}
      letterSpacing={1.2}
      sx={{ mb: 1, display: "block" }}
    >
      {children}
    </Typography>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const VendorOrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParams] = useSearchParams();

  const vendorId = searchParams.get("id");
  const userId = searchParams.get("uid");

  const fetchOrders = useCallback(async () => {
    setFetching(true);
    try { 
      const res = await apiClient.get("/api/admin/orders", {
        params: {
          search, status: statusFilter, page: page + 1, limit: rowsPerPage, vendorId: vendorId, userId: userId
        },
      });
      setOrders(res?.data?.data?.data || []);
      setTotalCount(res?.data?.data?.total || 0);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setFetching(false);
    }
  }, [search, statusFilter, page, rowsPerPage, userId, vendorId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const navigate = useNavigate()

  const pageChange = (url) => {
    navigate(url)
  }

  const openModal = (order) => {
    pageChange(pageRoute.orderView + order.id)
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box >

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Orders</Typography>

        </Box>

      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap">
          <TextField
            size="small" placeholder="Search order / customer…"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Status</MenuItem>
            {STATUS_FLOW.map((s) => (
              <MenuItem key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Payment" value={payFilter}
            onChange={(e) => { setPayFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Payments</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="pending">Unpaid</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </TextField>
          {(statusFilter || payFilter || search) && (
            <Chip
              icon={<FilterList />}
              label="Clear Filters"
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => { setStatusFilter(""); setPayFilter(""); setSearch(""); }}
              onClick={() => { setStatusFilter(""); setPayFilter(""); setSearch(""); }}
            />
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {fetching && <LinearProgress />}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.50" }}>
                {["Order", "Customer", "Amount", "Payment", "Status", "Placed On", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, py: 1.5, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.filter((o) => !payFilter || o.payment_status === payFilter).length === 0 && !fetching ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No orders found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders
                  .filter((o) => !payFilter || o.payment_status === payFilter)
                  .map((row) => {
                    const items = parseJSON(row.items, []);
                    const sc = STATUS_CONFIG[row.order_status] || STATUS_CONFIG.pending;
                    const pc = PAYMENT_CONFIG[row.payment_status] || PAYMENT_CONFIG.pending;
                    return (
                      <HoverRow key={row.id} onClick={() => openModal(row)}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.orderNumber}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: 11, bgcolor: "primary.light", color: "primary.dark" }}>
                              {initials(row.customerName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 130 }}>
                                {row.customerName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">{row.customerMobile}</Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>₹{row.grand_total?.toLocaleString("en-IN")}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.payment_type?.toUpperCase()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={pc.label} color={pc.color} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip icon={sc.icon} label={sc.label} color={sc.color} size="small" sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <CalendarToday sx={{ fontSize: 12, color: "text.disabled" }} />
                            <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                              {fmtDate(row.createdAt)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary">
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </HoverRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, np) => setPage(np)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      {/* ══════════════════════════════════════════
          ORDER DETAIL DIALOG
      ══════════════════════════════════════════ */}
    </Box>
  );
};

export default VendorOrderManagement;